import api from '../../api';
const cloneDeep = require('lodash/cloneDeep');
const axios = require('axios');

const baseSort = { col: 'updated_at', dir: 'desc' };
const baseFilter = {
    is_read: '',
    assigned_to_user_id: '',
    assigned_to_box_id: '',
    created_by: '',
    is_archived: false,
    is_public: false,
    statuses: ['Active'],
    'date#created_at#from': '',
    'date#created_at#to': '',
    'date#created_at#on': '',
    'date#updated_at#from': '',
    'date#updated_at#to': '',
    'date#updated_at#on': '',
};

export default {
    namespaced: true,
    state: {
        boxes: [],
        terms: '',
        pageIndex: 0,
        itemsPerPage: 10,
        selectedItemIds: [],
        items: [],
        totalItems: 0,
        counts: {},
        countsLastLoaded: 0,
        sort: cloneDeep(baseSort),
        filter: cloneDeep(baseFilter),
    },
    getters: {
        boxes(state) {
            return state.boxes
        },
        boxById: state => boxId => {
            return state.boxes.find(box => box.id === boxId);
        },
        accessorsByBoxId: state => boxId => {
            let box = state.boxes.find(box => box.id === boxId);
            let directAccessors = box ? box.accessors : [];
            let indirectAccessors = [];

            // add indirect accessors (through group box)
            for (let b of state.boxes) {
                if (b.id === boxId || b.type !== 'group') continue;
                if (b.boxes.includes(boxId)) indirectAccessors = [...indirectAccessors, ...b.accessors];
            }

            return directAccessors.concat(indirectAccessors);
        },
        boxesByAccessorId: (state, getters, rootState, rootGetters) => accessorId => {
            // get all user and org boxes that a user has access to
            let accessibleBoxes = state.boxes.reduce((boxes, box) => {
                if (box.accessors.includes(accessorId) || box.id === accessorId || rootGetters['auth/isSuperAdmin']) {
                    if (box.type === 'group') {
                        let bgBoxes = box.boxes.map(subBox => getters.boxById(subBox.id));
                        boxes = [...boxes, ...bgBoxes];
                    } else {
                        boxes.push(box);
                    }
                }
                return boxes;
            }, []);

            return accessibleBoxes
                .filter((box, index) => {
                    // remove duplicates and inactive boxes
                    if (!box.isActive) return false;
                    
                    let indexOfMatch = accessibleBoxes.findIndex(b => b.type === box.type && b.id === box.id);
                    return indexOfMatch === -1 || indexOfMatch === index;
                })
                .sort((a, b) => a.name > b.name ? 1 : -1);
        },
        defaultNotificationSetting: (state, getters, rootState, rootGetters) => box => {
            let userId = rootGetters['user/user'].id;
            if (box.type === 'org' || box.id === userId) return true;

            return false;
        },
        terms(state) {
            return state.terms;
        },
        pageIndex(state) {
            return state.pageIndex;
        },
        itemsPerPage(state) {
            return state.itemsPerPage;
        },
        selectedItemIds(state) {
            return state.selectedItemIds;
        },
        totalItems(state) {
            return state.totalItems;
        },
        items(state) {
            return state.items;
        },
        counts(state) {
            return state.counts;
        },
        countsLastLoaded(state) {
            return state.countsLastLoaded;
        },
        sort(state) {
            return state.sort;
        },
        filter(state) {
            return state.filter;
        },
        activeFilter(state, getters) {
            return Object.keys(getters.filter).reduce((acc, key) => {
                if (getters.filter[key] !== '') acc[key] = getters.filter[key];
                return acc;
            }, {});
        },
        searchParams(state, getters) {
            let offset = getters.pageIndex * getters.itemsPerPage;
            return {
                limit: getters.itemsPerPage,
                offset,
                terms: getters.terms,
                sort: getters.sort,
                filter: getters.filter,
            }
        },
        statuses() {
            let statuses = {
                Draft: {
                    color: '#f6f0e4'
                },
                Pending: {
                    color: '#e4e4f6'
                },
                Submitted: {
                    color: '#e4f6ed'
                },
                Archived: {
                    color: '#f6e5e4'
                }
            };
            
            // todo: add custom statuses here

            return statuses;
        },
        availableBoxes(state, getters, rootState, rootGetters) {
            let userId = rootGetters['user/user'].id;
            let boxes = [];
            let boxesInfo = getters.boxesByAccessorId(userId)
                .filter(boxInfo => {
                    // remove current user
                    return boxInfo.id !== userId
                })
                .sort((a, b) => {
                    // sort by box type (user first), then name
                    return (a.type === 'user' ? 'a' : 'z') + a.name > (b.type === 'user' ? 'a' : 'z') + b.name ? 1 : -1
                })

            // add current user's box first (if not a superadmin who doesn't belong to this org)
            if (!rootGetters['auth/isSuperAdmin']) {
                boxesInfo.unshift({ type: 'user', id: userId, name: 'My Inbox' });
            }

            for (let i = 0; i < boxesInfo.length; i++) {
                const boxInfo = boxesInfo[i];
                let folders = [
                    {
                        name: 'Inbox',
                        icon: 'far fa-inbox-in',
                        filter: {
                            ...cloneDeep(baseFilter),
                            assigned_to_user_id: boxInfo.type === 'user' ? boxInfo.id : '',
                            assigned_to_box_id: boxInfo.type === 'org' ? boxInfo.id : '',
                            statuses: ['Active', 'Pending with']
                        },
                        path: 'inbox',
                        visible: true,
                    },
                    {
                        name: 'Drafts',
                        icon: 'far fa-file-alt',
                        filter: {
                            ...cloneDeep(baseFilter),
                            created_by: boxInfo.type === 'user' ? boxInfo.id : '',
                            statuses: ['Draft']
                        },
                        path: 'drafts',
                        visible: boxInfo.type === 'user'
                    },
                    {
                        name: 'Pending',
                        icon: 'far fa-history',
                        filter: {
                            ...cloneDeep(baseFilter),
                            assigned_to_user_id: boxInfo.type === 'user' ? boxInfo.id : '',
                            assigned_to_box_id: boxInfo.type === 'org' ? boxInfo.id : '',
                            statuses: ['Pending']
                        },
                        path: 'pending',
                        visible: true
                    },
                    {
                        name: "Archive",
                        icon: 'far fa-archive',
                        filter: {
                            ...cloneDeep(baseFilter),
                            assigned_to_user_id: boxInfo.type === 'user' ? boxInfo.id : '',
                            assigned_to_box_id: boxInfo.type === 'org' ? boxInfo.id : '',
                            is_archived: true,
                            statuses: []
                        },
                        path: 'archive',
                        visible: true
                    },
                ];
                if (i === 0) {
                    folders.push({
                        name: 'Public',
                        icon: 'far fa-sign',
                        filter: {
                            ...cloneDeep(baseFilter),
                            statuses: [],
                            is_public: true
                        },
                        path: 'public',
                        visible: false
                    });
                }
                boxes.push({ info: boxInfo, folders });
            }

            return boxes;
        },
        sortGroups() {
            return [
                { name: 'Form Name', col: 'title' },
                { name: 'Last Updated', col: 'updated_at' },
                { name: 'Assigned To', col: 'assigned_to_name' },
                { name: 'Status', col: 'status' },
            ];
        },
        activeSortGroup(state, getters) {
            return getters.sortGroups.find(group => group.col === getters.sort.col) || {};
        },
        sections(state, getters, rootState, rootGetters) {
            let w = rootGetters['page/width'];
            let items = getters.items;
            let SM = 900;
            let MD = 1200;
            let LG = 1400;
            let XL = 1600;

            let sections = {
                checkbox: {
                    width: '35px',
                    visible: true
                },
                view: {
                    width: '85px',
                    visible: true
                },
                title: {
                    width: '100%',
                    visible: true
                },
                assigned_to_name: {
                    width: '150px',
                    visible: w > LG && items && items.some(item => item.assigned_to_name)
                },
                created_at: {
                    width: '100px',
                    visible: w > XL
                },
                tag_data: {
                    visible: w > SM,
                },
                status: {
                    width: '100px',
                    visible: w > MD
                },
                link: {
                    width: '50px',
                    visible: w > SM
                },
                print: {
                    width: '50px',
                    visible: w > SM
                },
                archive: {
                    width: '50px',
                    visible: true
                },
                sending_to: {
                    width: '150px',
                    visible: w > MD && items && items.some(item => item.is_public)
                },
            }

            let spaceAvailableForTags = Object.keys(sections).reduce((remainingSpace, sectionName) => {
                if (sectionName === 'tag_data' || !sections[sectionName].visible) return remainingSpace;
                if (sectionName === 'title') return remainingSpace - 200;

                return remainingSpace - parseInt(sections[sectionName].width);
            }, w - 400);

            sections.tag_data.maxTagsVisible = Math.floor(spaceAvailableForTags / 175);
            return sections;
        },
        stringifyBox: state => box => {
            return `${box.type}_${box.id}`;
        },
        parseBoxString: state => (assigneeStr) => {
            if (!assigneeStr) return false;

            let type = 'user';
            let otherType = 'box';
            let id = assigneeStr;

            if (assigneeStr.substr(0, 4) === 'box_') {
                otherType = 'user';
                type = 'box';
                id = assigneeStr.substring(4);
            }

            return { type, otherType, id }
        },
    },
    mutations: {
        setBoxes(state, boxes) {
            state.boxes = boxes;
        },
        setTerms(state, terms) {
            state.terms = terms;
        },
        setPageIndex(state, pageIndex) {
            state.pageIndex = parseInt(pageIndex);
        },
        setItemsPerPage(state, itemsPerPage) {
            state.itemsPerPage = parseInt(itemsPerPage);
        },
        setSelected(state, selectedItemIds) {
            state.selectedItemIds = selectedItemIds;
        },
        setItems(state, items) {
            state.items = items;
        },
        setTotalItems(state, totalItems) {
            state.totalItems = parseInt(totalItems);
        },
        setCounts(state, counts) {
            state.counts = counts;
        },
        setCountsLastLoaded(state, timestamp) {
            state.countsLastLoaded = timestamp;
        },
        setSort(state, sort) {
            state.sort = sort;
        },
        setFilter(state, filter) {
            state.filter = filter;
        },
        updateItem(state, { index, item }) {
            state.items.splice(index, 1, item)
        },
        updateFilterProp(state, { name, value }) {
            state.filter[name] = value;

            // allow only one sending_to and one assigned_to filter
            if (name === 'sending_to_box_id' && value) state.filter.sending_to_user_id = '';
            if (name === 'sending_to_user_id' && value) state.filter.sending_to_box_id = '';
            if (name === 'assigned_to_box_id' && value) state.filter.assigned_to_user_id = '';
            if (name === 'assigned_to_user_id' && value) state.filter.assigned_to_box_id = '';

            // if no assigned to user or box, we don't need 'Pending with'
            if ((name === 'assigned_to_box_id' || name === 'assigned_to_user_id') && (!state.filter.assigned_to_box_id && !state.filter.assigned_to_user_id)) {
                let pendingWithIndex = state.filter.statuses.indexOf('Pending with');
                if (pendingWithIndex !== -1) state.filter.statuses.splice(pendingWithIndex, 1);
            }
        }
    },
    actions: {
        async updateSelected({ getters, dispatch }, updates) {
            await dispatch('package/updateMultiple', { packageIds: getters.selectedItemIds, updates }, { root: true });
            dispatch('load');
            dispatch('loadCounts', 0);
        },
        async archiveSelected({ getters, dispatch }, archive) {
            let wereArchived = await dispatch('archivePkgs', { archive, packageIds: getters.selectedItemIds });
            if (!wereArchived) return;

            dispatch('load');
        },
        async archivePkgs({ dispatch }, { archive, packageIds }) {
            let wereArchived = await dispatch('package/archivePkgs', { packageIds, archive }, { root: true });
            if (!wereArchived) return;

            let archiveString = `${packageIds.length} item${packageIds.length === 1 ? '' : 's'}`;
            dispatch(
                'notification/success',
                `${archiveString} ${archive ? 'archived' : 'unarchived'}`,
                { root: true }
            );

            dispatch('load');
            dispatch('loadCounts', 0);
        },
        async deleteDrafts({ getters, dispatch }, packageIds = null) {
            if (!packageIds) packageIds = getters.selectedItemIds;
            let wereDeleted = await dispatch('package/deleteDrafts', { packageIds }, { root: true });
            if (!wereDeleted) return;

            let deleteString = packageIds.length === 1 ? `Draft` : `${packageIds.length} drafts`;
            dispatch('notification/success', `${deleteString} deleted`, { root: true });

            dispatch('load');
            dispatch('loadCounts', 0);
        },
        async deleteOrArchive({ getters, dispatch }, pkgIds = null) {
            if (!pkgIds) pkgIds = getters.selectedItemIds;
            let res = await dispatch('dialog/set', {
                type: 'confirm',
                header: `Delete ${pkgIds.length > 1 ? `${pkgIds.length} public links` : 'public link'}?`,
                critical: true,
                body: `This cannot be undone.`,
            }, { root: true });;
            if (!res) return;

            let deleteIds = [], archiveIds = [];
            for (let id of pkgIds) {
                let pkg = getters.items.find(item => item.id === id);
                if (pkg.status === 'Draft') {
                    deleteIds.push(id);
                } else {
                    archiveIds.push(id);
                }
            }
            if (deleteIds.length) {
                await dispatch('package/deleteDrafts', { packageIds: deleteIds, skipConfirm: true }, { root: true });
            }
            if (archiveIds.length) {
                await dispatch('package/archivePkgs', { packageIds: archiveIds, skipConfirm: true }, { root: true });
            }

            let deleteString = pkgIds.length > 1 ? `${pkgIds.length} public links` : 'Public link';
            dispatch('notification/success', `${deleteString} deleted`, { root: true });

            await dispatch('load');
            dispatch('loadCounts', 0);
        },
        async deletePackages({ getters, dispatch }, pkgIds = null) {
            if (!pkgIds) pkgIds = getters.selectedItemIds;
            let wereDeleted = await dispatch('package/deletePackages', pkgIds, { root: true });
            if (!wereDeleted) return;

            let deleteString = pkgIds.length === 1 ? `Form package` : `${pkgIds.length} form packages`;
            dispatch('notification/success', `${deleteString} deleted`, { root: true });

            dispatch('load');
            dispatch('loadCounts', 0);
        },
        async loadCounts({ getters, commit, rootGetters }, throttle = 5000) {
            if (rootGetters['user/requiresOnboarding']) return;
            
            // set throttle to 0 to force load
            if (Date.now() - getters.countsLastLoaded < throttle) {
                return;
            }

            commit('setCountsLastLoaded', Date.now());
            let counts = await api.get('packages/counts');

            commit('setCounts', counts);
        },
        async selectAllItems({ getters, dispatch, commit }) {
            let ids = await dispatch('package/search', { ...getters.searchParams, returns: 'ids' }, { root: true });
            commit('setSelected', ids);
        },
        initFilter({ rootGetters, commit }) {
            let filter = cloneDeep(baseFilter);
            filter.assigned_to_user_id = rootGetters['auth/isSuperAdmin']
                ? Object.keys(rootGetters['org/users'])[0]
                : rootGetters['user/user'].id;
            commit('setFilter', filter);
        },
        async load({ dispatch, getters, rootGetters, commit }, reqId = '') {
            if (getters.selectedItemIds.length) commit('setSelected', []);

            try {
                let res = await dispatch('package/search', { ...getters.searchParams, cancellable: true }, { root: true });

                // the api only sends back assigned_to_name if it's needed for sorting, so we'll add it if not existing
                let pkgs = res.pkgs.map(pkg => {
                    if (pkg.assigned_to_user_id && !pkg.assigned_to_name) {
                        pkg.assigned_to_name = rootGetters['org/users'][pkg.assigned_to_user_id].name;
                    } else if (pkg.assigned_to_box_id && !pkg.assigned_to_name) {
                        pkg.assigned_to_name = getters.boxById(pkg.assigned_to_box_id).name;
                    }

                    return pkg;
                });

                commit('setItems', pkgs);
                commit('setTotalItems', res.count);
                commit('base/setLoading', {isLoading: false}, { root: true });
            } catch (e) {
                if (!axios.isCancel(e)) throw (e);
            }

            return reqId;
        },
    }
}
